/**
 * @file: Join a date control and a time control into one moment, find the day
 * on which an interval ends, and write a time or a length as text.
 *
 * The interval panel holds the day and the clock time in two inputs, because a
 * single control for both is harder to correct. The two values are one moment,
 * and this file makes it.
 *
 * The midnight rule sets the end day from the clock times. An end time at or
 * before the start time is on the next day, so 22:00 to 02:00 is not backwards.
 *
 * A time field reports each digit as a new time. The rule must therefore give
 * the same day back when the digits move on. It applies only while the
 * operator has not chosen the end day, so it never makes an interval longer
 * than the operator asked for.
 *
 * The text formats are in this file so that the panel, the delete question
 * and the error messages write a time in one way. A time without its day is
 * ambiguous for an interval that crosses midnight.
 */

import moment from 'moment'

/**
 * Join a date and a time into one moment.
 *
 * The day comes from the first value and the clock reading from the second.
 * Both arrive as Date objects from the inputs.
 *
 * @param {Date|string} datePart - The value that carries the day
 * @param {Date|string} timePart - The value that carries the clock time
 * @returns {Object|null} A moment, or null when either value cannot be read
 */
function combineDateAndTime (datePart, timePart) {
  if (!datePart || !timePart) {
    return null
  }

  const date = moment(datePart)
  const time = moment(timePart)

  if (!date.isValid() || !time.isValid()) {
    return null
  }

  return date.clone()
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
}

const minutesOf = value => moment(value).hour() * 60 + moment(value).minute()

const overnightOffset = ({ startTime, endTime }) => minutesOf(endTime) <= minutesOf(startTime) ? 1 : 0

/** This function counts whole days from the start day to the end day. It gives null while a date is empty. */
function dayOffset ({ startDate, endDate }) {
  if (!startDate || !endDate) {
    return null
  }

  return moment(endDate).startOf('day').diff(moment(startDate).startOf('day'), 'days')
}

/**
 * The operator chose the end day when it is later than the day that the
 * midnight rule gives. An earlier end day puts the end before the start, so
 * the rule corrects it.
 */
function endDayIsChosen (fields) {
  const offset = dayOffset(fields)

  if (offset === null || !fields.startTime || !fields.endTime) {
    return false
  }

  return offset > overnightOffset(fields)
}

function overnightEndDate (fields) {
  return moment(fields.startDate).add(overnightOffset(fields), 'days').toDate()
}

/** This dash separates a start from an end. The calendar bars use the same dash. */
const RANGE_DASH = '–'

const MOMENT_FORMAT = 'ddd DD/MM HH:mm'
const CLOCK_FORMAT = 'HH:mm'
const MINUTES_IN_HOUR = 60
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR

/**
 * Write one moment as "Sat 12/09 21:00".
 *
 * @param {Date|string|Object} value - A Date, a string or a moment
 * @returns {string} An empty string when the value cannot be read
 */
function formatMoment (value) {
  const read = moment(value)

  return value && read.isValid() ? read.format(MOMENT_FORMAT) : ''
}

/**
 * Write a range as "Sat 12/09 21:00–23:00". The end gets its own day only when
 * it is on another day: "Fri 11/09 23:00–Sat 12/09 01:30".
 *
 * @param {Date|string|Object} start
 * @param {Date|string|Object} end
 * @returns {string} An empty string when either value cannot be read
 */
function formatRange (start, end) {
  const from = formatMoment(start)
  const to = formatMoment(end)

  if (!from || !to) {
    return ''
  }

  const sameDay = moment(start).isSame(moment(end), 'day')

  return `${from}${RANGE_DASH}${sameDay ? moment(end).format(CLOCK_FORMAT) : to}`
}

/**
 * Write the length from start to end as "45 min", "4 h", "2 h 15 min" or
 * "1 day 2 h 15 min". A part that is zero is left out.
 *
 * @param {Date|string|Object} start
 * @param {Date|string|Object} end
 * @returns {string} An empty string when the end is not after the start
 */
function formatLength (start, end) {
  const minutes = moment(end).diff(moment(start), 'minutes')

  if (!(minutes > 0)) {
    return ''
  }

  const days = Math.floor(minutes / MINUTES_IN_DAY)
  const hours = Math.floor((minutes % MINUTES_IN_DAY) / MINUTES_IN_HOUR)
  const rest = minutes % MINUTES_IN_HOUR

  return [
    days ? `${days} ${days === 1 ? 'day' : 'days'}` : '',
    hours ? `${hours} h` : '',
    rest ? `${rest} min` : ''
  ].filter(Boolean).join(' ')
}

export {
  combineDateAndTime, dayOffset, endDayIsChosen, overnightEndDate, formatMoment, formatRange, formatLength,
  MINUTES_IN_DAY, RANGE_DASH
}
